import _ from 'lodash';
import { useMemo } from 'react';

import useDebounce from '../../../../../hooks/useDebounce/useDebounce';
import useSearchParams from '../../../../../hooks/useSearchParams';
import { useUserContextPreferences } from '../../../../../hooks/useUserContextPreferences';
import LangEnum from '../../../../../lang/LangEnum';
import { getNumberOfSelectedFilters } from '../../../../../lib/FilterLib';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { useUpdateOrCreateFilterPreference } from '../../../../../services/modules/userContextPreferences/userContextPreferences.mutations';
import { FilterPreferenceRecognizedLists } from '../../../../../services/modules/userContextPreferences/userContextPreferences.types';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { FilterProps } from '../../sharedBetweenFilters/types';

interface UseSavableHideableFilterProps {
  filters: FilterProps<true>[];
  list: FilterPreferenceRecognizedLists;
  ignoredFilterKeys?: string[];
}

const useSavableHideableFilter = ({
  filters,
  list,
  ignoredFilterKeys,
}: UseSavableHideableFilterProps) => {
  const allFiltersKeys = filters.flatMap((filter) => filter.getSearchParamKeys());

  const { handleMessage } = snackbarStore;
  const { translate } = useTranslation();
  const { searchParams } = useSearchParams();

  const { mutate, isPending: isSavingFilters } = useUpdateOrCreateFilterPreference();

  const { getDefaultFiltersToList, hasFetchedUserPreferences } = useUserContextPreferences();

  const defaultFilters = getDefaultFiltersToList(list);

  const getCurrentAppliedFilters = () => {
    const appliedFilters: Record<string, any> = {};
    allFiltersKeys.forEach((key) => {
      const valueInSearchParams = searchParams[key];
      if (!valueInSearchParams) {
        return;
      }
      appliedFilters[key] = searchParams[key];
    });
    return appliedFilters;
  };

  const canRestoreToDefaultFilters = useMemo(() => {
    if (!hasFetchedUserPreferences || !defaultFilters || isSavingFilters) {
      return false;
    }

    const currentAppliedFilters = getCurrentAppliedFilters();
    const isSameFilters = _.isEqual(defaultFilters, currentAppliedFilters);

    return !isSameFilters;
  }, [hasFetchedUserPreferences, defaultFilters, isSavingFilters, searchParams]);

  const canSaveFilters = useMemo(() => {
    const currentAppliedFilters = getCurrentAppliedFilters();

    if (!hasFetchedUserPreferences || isSavingFilters) {
      return false;
    }

    if (!defaultFilters) {
      return true;
    }

    const isSameFilters = _.isEqual(defaultFilters, currentAppliedFilters);
    return !isSameFilters;
  }, [hasFetchedUserPreferences, defaultFilters, isSavingFilters, searchParams]);

  const totalAppliedFilters = getNumberOfSelectedFilters(
    searchParams,
    allFiltersKeys,
    ignoredFilterKeys,
  );
  const debouncedTotalAppliedFilters = useDebounce(totalAppliedFilters, 50);

  const saveFilters = () => {
    const currentAppliedFilters = getCurrentAppliedFilters();
    mutate(
      { list, defaultFilters: currentAppliedFilters },
      {
        onError: () => {
          handleMessage({
            type: 'error',
            message: translate(LangEnum.FILTER_ERROR_SAVING),
          });
        },
      },
    );
  };

  const restoreToDefaultFilters = async () => {
    await Promise.all(filters.map((filter) => filter.handleResetToDefaultValue()));
  };

  return {
    saveFilters,
    isSavingFilters,
    canSaveFilters,
    totalAppliedFilters: debouncedTotalAppliedFilters,
    restoreToDefaultFilters,
    canRestoreToDefaultFilters,
    hasAnyAppliedFilter: debouncedTotalAppliedFilters > 0,
  };
};

export default useSavableHideableFilter;
